import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import secureStorage from '../utils/secureStorage';
import { API_BASE_URL } from '../config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  X,
  Upload,
  FileText,
  Trash2,
  Download,
  Search,
  Shield,
  Users,
  UserCheck,
  Eye,
  Printer,
  File as FileIcon,
} from 'lucide-react';

const API = `${API_BASE_URL}/api`;

const VISIBILITY_META = {
  exec: { label: 'Exec only', icon: Shield, className: 'border-red-500/50 text-red-400' },
  coordinators: { label: 'Coordinators', icon: UserCheck, className: 'border-yellow-500/50 text-yellow-400' },
  all: { label: 'All Participants', icon: Users, className: 'border-green-500/50 text-green-400' },
};

const ALLOWED_EXTS = 'pdf,xls,xlsx,doc,docx,ppt,pptx,csv,txt,png,jpg,jpeg,gif,webp';
const ACCEPT_ATTR = ALLOWED_EXTS.split(',').map((e) => `.${e}`).join(',');
const MAX_MB = 25;

const humanSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const iconFor = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return { color: 'text-pink-400', label: ext.toUpperCase() };
  if (['pdf'].includes(ext)) return { color: 'text-red-400', label: 'PDF' };
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { color: 'text-green-400', label: ext.toUpperCase() };
  if (['doc', 'docx'].includes(ext)) return { color: 'text-blue-400', label: ext.toUpperCase() };
  if (['ppt', 'pptx'].includes(ext)) return { color: 'text-orange-400', label: ext.toUpperCase() };
  if (['txt'].includes(ext)) return { color: 'text-gray-400', label: 'TXT' };
  return { color: 'text-gray-400', label: ext.toUpperCase() || 'FILE' };
};

/**
 * ExerciseDocumentsModal — per-exercise document library.
 * Users upload/download/rename/delete files scoped to a single exercise, with
 * per-document visibility (exec / coordinators / all).
 */
const ExerciseDocumentsModal = ({ open, onClose, currentExercise }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [search, setSearch] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [tag, setTag] = useState('');

  const authHeaders = () => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    if (!currentExercise?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/exercise-documents`, {
        params: { exercise_id: currentExercise.id },
        headers: authHeaders(),
      });
      setDocs(res.data || []);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [currentExercise?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!currentExercise?.id) {
      toast.error('Select an exercise first');
      return;
    }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS.split(',').includes(ext)) {
      toast.error(`Unsupported .${ext}. Allowed: ${ALLOWED_EXTS}`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('exercise_id', currentExercise.id);
      form.append('visibility', visibility);
      form.append('tag', tag || '');
      const res = await axios.post(`${API}/exercise-documents/upload`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setDocs((prev) => [res.data, ...prev]);
      toast.success(`Uploaded ${res.data.original_name}`);
      setTag('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFilesInput = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    files.forEach(uploadFile);
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/exercise-documents/${doc.id}/download`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const _guessMime = (doc) => {
    if (doc.mime_type && doc.mime_type !== 'application/octet-stream') return doc.mime_type;
    const ext = (doc.original_name || '').split('.').pop().toLowerCase();
    const map = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      txt: 'text/plain',
      csv: 'text/csv',
      html: 'text/html',
      json: 'application/json',
      xml: 'application/xml',
    };
    return map[ext] || 'application/octet-stream';
  };

  const openInNewTab = async (doc) => {
    // IMPORTANT: open the tab synchronously inside the click handler so pop-up
    // blockers do not swallow the request. We then load content into it once
    // the fetch resolves.
    const win = window.open('', '_blank');
    if (!win) {
      toast.error('Please allow pop-ups to view files');
      return;
    }
    try {
      win.document.write(
        `<title>Loading ${doc.original_name || 'document'}…</title>` +
        `<p style="font-family:system-ui;padding:24px;color:#555">Loading ${doc.original_name || 'document'}…</p>`
      );
      const res = await axios.get(`${API}/exercise-documents/${doc.id}/download`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: _guessMime(doc) });
      const url = URL.createObjectURL(blob);
      // Point the already-open tab to the blob URL so the browser renders it inline
      win.location.replace(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // Try to parse the JSON detail from a blob-typed error response
      let msg = 'View failed';
      const data = e?.response?.data;
      try {
        if (data && typeof data.text === 'function') {
          const raw = await data.text();
          try { msg = JSON.parse(raw)?.detail || raw || msg; }
          catch { msg = raw || msg; }
        } else if (typeof data?.detail === 'string') {
          msg = data.detail;
        }
      } catch { /* noop */ }
      try {
        win.document.body.innerHTML = `<p style="font-family:system-ui;padding:24px;color:#c00">${msg}</p>`;
      } catch { /* noop */ }
      toast.error(msg);
    }
  };

  const printDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/exercise-documents/${doc.id}/download`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          window.open(url, '_blank');
        }
      };
      document.body.appendChild(iframe);
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch (err) { /* noop */ }
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Print failed');
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/exercise-documents/${doc.id}`, { headers: authHeaders() });
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success('Document deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const setDocVisibility = async (doc, newVis) => {
    try {
      const res = await axios.put(
        `${API}/exercise-documents/${doc.id}`,
        { visibility: newVis },
        { headers: authHeaders() }
      );
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? res.data : d)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const filtered = docs.filter((d) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return `${d.original_name} ${d.tag} ${d.uploaded_by_name}`.toLowerCase().includes(s);
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70 p-3">
      <Card className="bg-gray-900 border-orange-500/40 w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="min-w-0">
            <h2 className="text-orange-500 font-semibold text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Exercise Documents
            </h2>
            <p className="text-gray-400 text-xs truncate">
              {currentExercise?.exercise_name || 'No exercise selected'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-orange-400 p-2"
            data-testid="doc-modal-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!currentExercise?.id ? (
          <div className="p-8 text-center text-gray-400 flex-1">
            Select an exercise first from the Exercise Dashboard to upload documents.
          </div>
        ) : (
          <>
            {/* Upload zone */}
            <div className="p-4 border-b border-gray-700 space-y-3">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                  dragActive ? 'border-orange-500 bg-orange-500/10' : 'border-gray-600 bg-gray-800/40'
                }`}
                data-testid="doc-dropzone"
              >
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-300 text-sm mb-2">
                  Drop files here or click to browse
                </p>
                <p className="text-gray-500 text-xs mb-3">
                  {ALLOWED_EXTS.toUpperCase().replace(/,/g, ' · ')} · max {MAX_MB} MB
                </p>
                <input
                  type="file"
                  multiple
                  accept={ACCEPT_ATTR}
                  onChange={handleFilesInput}
                  className="hidden"
                  id="doc-file-input"
                  data-testid="doc-file-input"
                />
                <Button
                  onClick={() => document.getElementById('doc-file-input').click()}
                  disabled={uploading}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold min-h-[40px]"
                  data-testid="doc-browse-btn"
                >
                  {uploading ? 'Uploading…' : 'Browse files'}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-gray-300 text-xs">Visibility for uploads</Label>
                  <Select value={visibility} onValueChange={setVisibility}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-10" data-testid="doc-visibility-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="all">All Participants</SelectItem>
                      <SelectItem value="coordinators">Coordinators only</SelectItem>
                      <SelectItem value="exec">Exec only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Tag (optional)</Label>
                  <Input
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    placeholder="e.g. Plans, Evacuation, HIRA"
                    className="bg-gray-800 border-gray-700 text-white h-10"
                    data-testid="doc-tag-input"
                  />
                </div>
              </div>
            </div>

            {/* Search + list */}
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by filename, tag, or uploader…"
                  className="pl-9 bg-gray-800 border-gray-700 text-white h-10"
                  data-testid="doc-search"
                />
              </div>
              {loading ? (
                <div className="py-10 text-center text-gray-500 text-sm">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="py-10 text-center text-gray-500 text-sm">
                  {docs.length === 0 ? 'No documents yet — upload one above.' : 'No results.'}
                </div>
              ) : (
                <div className="space-y-2" data-testid="doc-list">
                  {filtered.map((d) => {
                    const meta = VISIBILITY_META[d.visibility] || VISIBILITY_META.all;
                    const MetaIcon = meta.icon;
                    const fi = iconFor(d.original_name);
                    return (
                      <div
                        key={d.id}
                        data-testid={`doc-row-${d.id}`}
                        className="flex items-center gap-3 bg-gray-800 border border-gray-700 rounded-lg p-3"
                      >
                        <div className={`flex-shrink-0 w-10 h-10 rounded-md bg-gray-900 border border-gray-700 flex items-center justify-center ${fi.color}`}>
                          <FileIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white font-medium truncate">{d.original_name}</p>
                            <Badge variant="outline" className="text-xs border-gray-600 text-gray-300">{fi.label}</Badge>
                            {d.tag && (
                              <Badge variant="outline" className="text-xs border-orange-500/50 text-orange-400">
                                {d.tag}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {humanSize(d.size_bytes)} · {d.uploaded_by_name || 'Unknown'}
                            {d.uploaded_at ? ` · ${new Date(d.uploaded_at).toLocaleString()}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Select value={d.visibility} onValueChange={(v) => setDocVisibility(d, v)}>
                            <SelectTrigger className={`h-9 w-[140px] bg-gray-900 text-xs ${meta.className}`} data-testid={`doc-vis-${d.id}`}>
                              <div className="flex items-center gap-1">
                                <MetaIcon className="h-3.5 w-3.5" />
                                <span className="truncate">{meta.label}</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700 text-white">
                              <SelectItem value="all">All Participants</SelectItem>
                              <SelectItem value="coordinators">Coordinators only</SelectItem>
                              <SelectItem value="exec">Exec only</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openInNewTab(d)}
                            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 min-h-[36px]"
                            data-testid={`doc-view-${d.id}`}
                            title="Open in a new tab"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => printDoc(d)}
                            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 min-h-[36px]"
                            data-testid={`doc-print-${d.id}`}
                            title="Print this document"
                          >
                            <Printer className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => download(d)}
                            className="border-gray-600 text-gray-300 hover:text-orange-400 min-h-[36px]"
                            data-testid={`doc-download-${d.id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => remove(d)}
                            className="border-red-500/40 text-red-400 hover:bg-red-500/10 min-h-[36px]"
                            data-testid={`doc-delete-${d.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default ExerciseDocumentsModal;
